import {
  clearZoomLensClip,
  updateZoomLensClip,
  useZoomLensClip,
} from "@/components/r3f/renderers/measurements/zoom-lens-clip";
import { ProjectionType } from "@/tools/multi-point-measures/multi-point-measures-controls-logic";
import { useSvg, useThreeEventTarget } from "@faro-lotv/app-component-toolbox";
import type { GUID } from "@faro-lotv/foundation";
import { assert, type ComposeFramebuffersPass } from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  DoubleSide,
  GLSL3,
  Mesh,
  MOUSE,
  PlaneGeometry,
  RawShaderMaterial,
  Vector2,
  Vector3,
  type Camera,
  type IUniform,
  type Texture,
} from "three";
import { useToolsAcquaintance } from "../tools/tools-acquaintance-context";
import MeasurementLens from "./MeasurementLens.svg";

export const ZOOM_LENS_FRAME = "Zoom lens frame";

// The frame must draw last in the cosmetics pass, on top of the measurement lines/handlers.
const ZOOM_LENS_FRAME_RENDER_ORDER = 1000;

type LensFrame = Mesh<PlaneGeometry, LensFrameMaterial>;

/**
 *
 * @param composer The object that composes the FBOs with a zoom lens pattern
 * @param zoomLensOn Whether the zoom lens is active or not
 * @param pointCloudId GUID of the current point cloud
 */
export function useZoomLens(
  composer: RefObject<ComposeFramebuffersPass | null>,
  zoomLensOn: boolean,
  pointCloudId?: GUID,
): void {
  // Load the zoom lens SVG texture
  const map = useSvg(MeasurementLens, {
    width: ZOOM_LENS_RADIUS * 2,
    height: ZOOM_LENS_RADIUS * 2,
    generateMipmaps: false,
  });

  const [lensFrame, setLensFrame] = useState<LensFrame | null>(null);

  // When the zoom lens texture is available, create the 'lensFrame' mesh object
  useEffect(() => {
    if (map) {
      const material = new LensFrameMaterial();
      material.uniforms.uMap.value = map;
      const result = new Mesh<PlaneGeometry, LensFrameMaterial>(
        new PlaneGeometry(2, 2),
        material,
      );
      result.name = ZOOM_LENS_FRAME;
      result.frustumCulled = false;
      result.visible = true;
      result.renderOrder = ZOOM_LENS_FRAME_RENDER_ORDER;

      setLensFrame(result);

      return () => {
        material.dispose();
        result.geometry.dispose();
      };
    }
  }, [map]);

  const element = useThreeEventTarget();
  const scene = useThree((state) => state.scene);
  const dpr = useThree((state) => state.gl.getPixelRatio());
  const { pickingCurrentPoint, onValidClickInZoomLens } =
    useToolsAcquaintance();
  const camera = useThree((state) => state.camera);

  const targetGreenRef = useRef<boolean>(false);

  const clip = useZoomLensClip();

  const onMouseMove = useCallback(
    (ev: PointerEvent) => {
      assert(composer.current, "useZoomLens: composer should not be null here");
      if (!lensFrame) {
        return;
      }
      const rect = element.getBoundingClientRect();
      const evx = ev.clientX - rect.left;
      const evy = ev.clientY - rect.top;
      let centerX = evx + ZOOM_LENS_RADIUS;
      let mirrorX = 1;
      if (centerX > element.clientWidth) {
        centerX = evx - ZOOM_LENS_RADIUS;
        mirrorX = -1;
      }
      let centerY = evy - ZOOM_LENS_RADIUS;
      let mirrorY = 1;
      if (centerY < 0) {
        centerY = evy + ZOOM_LENS_RADIUS;
        mirrorY = -1;
      }

      assignZoomLensComposerUniforms(
        composer.current,
        centerX,
        centerY,
        evx,
        evy,
        dpr,
      );

      targetGreenRef.current = assignLensFrameUniforms(
        lensFrame,
        pickingCurrentPoint,
        camera,
        element,
        centerX,
        centerY,
        evx,
        evy,
        mirrorX,
        mirrorY,
        clip.projectionTypeRef.current !== ProjectionType.DoNotProject,
      );

      // Keep the measurement clip (3D lines/handlers and HTML labels) aligned with the lens circle.
      updateZoomLensClip(clip, {
        center: { x: centerX, y: centerY },
        target: { x: evx, y: evy },
        radius: ZOOM_LENS_RADIUS,
        dpr,
        viewWidth: element.clientWidth,
        viewHeight: element.clientHeight,
      });
    },
    [composer, lensFrame, element, dpr, pickingCurrentPoint, camera, clip],
  );

  // If the model is not being hovered, but the zoom lens contains a green target,
  // the click should be registered as valid
  const pointerDown = useRef<{ x: number; y: number } | null>(null);

  // Point picked to understand if the click was a drag or not
  const onPointerDown = useCallback((ev: PointerEvent) => {
    pointerDown.current = { x: ev.clientX, y: ev.clientY };
  }, []);

  const onPointerUp = useCallback(
    (ev: PointerEvent) => {
      const down = pointerDown.current;
      pointerDown.current = null;
      // A pointer up that ends a drag rotated the camera, it did not pick a point
      const isDrag =
        !down ||
        // Find the distance traveled by the pointer between down and up
        Math.hypot(ev.clientX - down.x, ev.clientY - down.y) >
          MAX_CLICK_MOVEMENT_IN_PIXELS;
      if (
        ev.button === MOUSE.LEFT &&
        !isDrag &&
        targetGreenRef.current &&
        !isNaN(pickingCurrentPoint.x) &&
        !!pointCloudId
      ) {
        ev.stopPropagation();
        onValidClickInZoomLens.emit(pointCloudId);
      }
    },
    [onValidClickInZoomLens, pointCloudId, pickingCurrentPoint],
  );

  // The clip is owned by 'zoomLensOn' alone: clearing it in the listener hook below would wipe it on
  // any re-run of that hook, with nothing but the next pointer move to bring it back.
  useEffect(() => {
    if (!zoomLensOn) return;
    return () => clearZoomLensClip(clip);
  }, [zoomLensOn, clip]);

  // This hook reacts on changes of the 'zoomLensOn' prop. When the zoom lens
  // is on, the 'lensFrame' mesh is attached to the 3js scene and the mouse move
  // listener is attached to the html view element, so that the zoom lens can follow
  // the mouse. When zoom lens is off, the lens frame is removed from the scene and the
  // mouse move listener is detached.
  useEffect(() => {
    if (!composer.current || !zoomLensOn || !lensFrame) return;
    element.addEventListener("pointermove", onMouseMove);
    element.addEventListener("pointerdown", onPointerDown);
    element.addEventListener("pointerup", onPointerUp);
    scene.add(lensFrame);
    return () => {
      element.removeEventListener("pointermove", onMouseMove);
      element.removeEventListener("pointerdown", onPointerDown);
      element.removeEventListener("pointerup", onPointerUp);
      scene.remove(lensFrame);
    };
  }, [
    composer,
    zoomLensOn,
    element,
    onMouseMove,
    scene,
    lensFrame,
    onPointerDown,
    onPointerUp,
  ]);
}

function assignZoomLensComposerUniforms(
  composer: ComposeFramebuffersPass,
  centerX: number,
  centerY: number,
  evx: number,
  evy: number,
  dpr: number,
): void {
  // When assigning values to the zoom lens composer uniforms,
  // we must account for the screen DPR, because the FBO composer
  // in the main rendering pipeline works with textures at maximum
  // resolution
  const { uniforms } = composer.zoomLensComposerMaterial;
  uniforms.uLensCenter.value.set(centerX * dpr, centerY * dpr);
  uniforms.uLensTarget.value.set(evx * dpr, evy * dpr);
  uniforms.uLensRadius.value = ZOOM_LENS_RADIUS * dpr;
}

function assignLensFrameUniforms(
  lensFrame: LensFrame,
  pickingCurrentPoint: Vector3,
  camera: Camera,
  element: HTMLElement,
  centerX: number,
  centerY: number,
  evx: number,
  evy: number,
  mirrorX: number,
  mirrorY: number,
  isProjecting: boolean,
): boolean {
  // All computations on the lens frame uniforms are done in pre-DPR screen coordinates,
  // to make it easier to account for the lens frame texture size and to express the
  // lens radius in pixels
  let targetPointGreen = false;
  let deltaX = 0;
  let deltaY = 0;
  if (!isNaN(pickingCurrentPoint.x)) {
    TARGET_POINT.copy(pickingCurrentPoint).project(camera);
    TARGET_POINT.x = (TARGET_POINT.x + 1) / 2;
    TARGET_POINT.y = (1 - TARGET_POINT.y) / 2;
    TARGET_POINT.x *= element.clientWidth;
    TARGET_POINT.y *= element.clientHeight;
    deltaX = TARGET_POINT.x - evx;
    deltaY = TARGET_POINT.y - evy;
    // If the measuring hovered point is being projected onto an horizontal or vertical
    // plane, then the click is possible also if the hovered point is outside the
    // zoom lens, and the target point should paint green to convey that it is still clickable
    targetPointGreen =
      isProjecting ||
      deltaX * deltaX + deltaY * deltaY < ZOOM_LENS_RADIUS * ZOOM_LENS_RADIUS;
  }

  const frameUniforms = lensFrame.material.uniforms;
  // When assigning values to the lens frame uniforms, coordinates
  // can also forget about DPR, because in the line below the resolution
  // is set without DPR.
  frameUniforms.uResolution.value.set(
    element.clientWidth,
    element.clientHeight,
  );
  if (targetPointGreen) {
    frameUniforms.uTargetPoint.value.set(
      0.5 * (1.0 + mirrorX * deltaX * INV_ZOOM_LENS_RADIUS),
      0.5 * (1.0 - mirrorY * deltaY * INV_ZOOM_LENS_RADIUS),
    );
  } else {
    frameUniforms.uTargetPoint.value.set(0.5, 0.5);
  }
  frameUniforms.uLensRadius.value = ZOOM_LENS_RADIUS;
  frameUniforms.uLensCenter.value.set(centerX, element.clientHeight - centerY);
  frameUniforms.uLensMirror.value.set(mirrorX, mirrorY);
  frameUniforms.uTargetPointGreen.value = targetPointGreen;
  return targetPointGreen;
}

const TARGET_POINT = new Vector3();

const vertexShader = `
in vec3 position;
in vec2 uv;

out vec2 vUv;

void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
}
`;

const fragmentShader = `
precision highp float;

out vec4 outColor;

uniform sampler2D uMap;
uniform vec2 uResolution;
uniform vec2 uLensCenter;
uniform vec2 uLensMirror;
uniform float uLensRadius;
uniform bool uTargetPointGreen;
uniform vec2 uTargetPoint;

const float k_TargetPointOpacity = 0.6;

in vec2 vUv;

void main()
{
    vec2 pos = vUv * uResolution;
    vec2 delta = pos - uLensCenter;
    if(abs(delta.x) > uLensRadius || abs(delta.y) > uLensRadius)
    {
        discard;
    }
    vec2 deltaUv = 0.5 * delta * uLensMirror / uLensRadius + vec2(0.5, 0.5);
    bool withinTargetDot = length(deltaUv - uTargetPoint) < 0.03;
    if (withinTargetDot) {
      if(uTargetPointGreen) {
        outColor = vec4(0.0, 1.0, 0.0, k_TargetPointOpacity);
      } else {
        outColor = vec4(1.0, 0.0, 0.0, 1.0);
      }
    } else {
      outColor = texture(uMap, deltaUv);
    }
}
`;

class LensFrameMaterial extends RawShaderMaterial {
  override vertexShader = vertexShader;
  override fragmentShader = fragmentShader;

  override name = "LensFrameMaterial";
  override uniforms = {
    uMap: makeUniform<Texture | null>(null),
    uResolution: makeUniform(new Vector2(100, 100)),
    uLensCenter: makeUniform(new Vector2(0, 0)),
    uLensRadius: makeUniform(0),
    uLensMirror: makeUniform(new Vector2(1, 1)),
    uTargetPointGreen: makeUniform(false),
    uTargetPoint: makeUniform(new Vector2(0.5, 0.5)),
  };

  constructor() {
    super({
      glslVersion: GLSL3,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: DoubleSide,
    });
  }
}

function makeUniform<T>(initialVal: T): IUniform<T> {
  return { value: initialVal };
}

const ZOOM_LENS_RADIUS = 70;
const INV_ZOOM_LENS_RADIUS = 1 / ZOOM_LENS_RADIUS;

/** Maximum pointer movement between down and up that is still considered a click and not a drag */
const MAX_CLICK_MOVEMENT_IN_PIXELS = 2;
